import React, { useState, useRef, useEffect } from 'react'
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline'
import ButtonEl from '../../ctas/Button'

// Format a calendar day as a local YYYY-MM-DD string. Using new Date(...).toISOString()
// converts the local midnight to UTC, which shifts the date back a day in positive-offset
// timezones (e.g. AEST +10:00 turns 23 Apr into 22 Apr) — the cause of NDS-148.
const toISODate = (year, month, day) =>
  `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`

const CalendarPicker = ({ dateFrom, dateTo, onApply, onCancel }) => {
  const [currentDate, setCurrentDate] = useState(() => {
    if (dateFrom) {
      const [y, m, d] = dateFrom.split('-').map(Number)
      return new Date(y, m - 1, d)
    }
    return new Date()
  })
  const [tempFrom, setTempFrom] = useState(dateFrom)
  const [tempTo, setTempTo] = useState(dateTo)
  const calendarRef = useRef(null)

  // Calendar logic: Days in month and Start Day offset
  const year = currentDate.getFullYear()
  const month = currentDate.getMonth()
  const monthName = currentDate.toLocaleString('default', { month: 'long' })

  const daysInMonth = new Date(year, month + 1, 0).getDate()
  const firstDayOfMonth = new Date(year, month, 1).getDay()
  // Adjust to Monday start (0=Mon, 6=Sun)
  const startOffset = firstDayOfMonth === 0 ? 6 : firstDayOfMonth - 1

  const handleDateClick = (day) => {
    const selected = toISODate(year, month, day)

    if (!tempFrom || (tempFrom && tempTo)) {
      setTempFrom(selected)
      setTempTo(null)
    } else if (selected < tempFrom) {
      setTempTo(tempFrom)
      setTempFrom(selected)
    } else {
      setTempTo(selected)
    }
  }

  const isSelected = (day) => {
    const dateStr = toISODate(year, month, day)
    return dateStr === tempFrom || dateStr === tempTo
  }

  const isInRange = (day) => {
    const dateStr = toISODate(year, month, day)
    return tempFrom && tempTo && dateStr > tempFrom && dateStr < tempTo
  }

  // Accessibility: Focus Trap logic
  useEffect(() => {
    const focusableElements = calendarRef.current.querySelectorAll('button, input')
    const firstElement = focusableElements[0]
    const lastElement = focusableElements[focusableElements.length - 1]

    const handleTab = (e) => {
      if (e.key !== 'Tab') return
      if (e.shiftKey) {
        if (document.activeElement === firstElement) {
          lastElement.focus()
          e.preventDefault()
        }
      } else {
        if (document.activeElement === lastElement) {
          firstElement.focus()
          e.preventDefault()
        }
      }
    }
    document.addEventListener('keydown', handleTab)
    return () => document.removeEventListener('keydown', handleTab)
  }, [])

  return (
    <div
      ref={calendarRef}
      role="dialog"
      aria-modal="true"
      aria-label={`Select Date Range for ${monthName} ${year}`}
      // Lifts the containing section above later ones for as long as this is mounted (see .inner in App.css)
      data-overlay
      className="absolute top-full left-0 mt-3 p-6 bg-white border border-gray-200 shadow-2xl rounded-xl w-[320px] z-[100] animate-in fade-in zoom-in-95"
    >
      {/* Month Header */}
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-xl font-bold text-(--color-secondary-text)">
          {monthName} {year}
        </h3>
        <div className="flex gap-4">
          <button
            type="button"
            onClick={() => setCurrentDate(new Date(year, month - 1))}
            aria-label="Previous Month"
            className="p-1 hover:bg-gray-100 rounded-full transition-colors"
          >
            <ChevronLeftIcon className="w-5 h-5 text-(--color-secondary-text)" />
          </button>
          <button
            type="button"
            onClick={() => setCurrentDate(new Date(year, month + 1))}
            aria-label="Next Month"
            className="p-1 hover:bg-gray-100 rounded-full transition-colors"
          >
            <ChevronRightIcon className="w-5 h-5 text-(--color-secondary-text)" />
          </button>
        </div>
      </div>

      {/* Calendar Grid */}
      <div role="grid" className="grid grid-cols-7 gap-y-1 gap-x-5 mb-6">
        {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((day) => (
          <div key={day} role="columnheader" className="text-center text-xs font-medium text-gray-400 pb-2">
            {day}
          </div>
        ))}

        {/* Empty slots for start of month */}
        {Array(startOffset)
          .fill(0)
          .map((_, i) => (
            <div key={`empty-${i}`} />
          ))}

        {/* Month Days */}
        {Array.from({ length: daysInMonth }, (_, i) => i + 1).map((day) => {
          const selected = isSelected(day)
          const inRange = isInRange(day)

          return (
            <button
              key={day}
              type="button"
              role="gridcell"
              aria-selected={selected || inRange}
              onClick={() => handleDateClick(day)}
              className={`relative h-10 w-10 text-sm font-medium transition-all flex items-center justify-center group
                ${inRange ? 'bg-(--sb-primary-shadow-bg)' : 'bg-transparent'}
                ${selected ? 'bg-(--sb-primary-bg)! text-(--color-light)! rounded-full z-10' : 'text(--sb-common-base-body-text-color) hover:bg-(--sb-secondary-shadow-bg) rounded-full'}
              `}
            >
              {day}
            </button>
          )
        })}
      </div>

      {/* Actions */}
      <div className="flex flex-col gap-3">
        <ButtonEl
          theme="primary"
          onClick={() => onApply(tempFrom, tempTo)}
          className="w-full py-3 rounded-full! font-bold text-sm text-(--color-light)! uppercase tracking-wider transition-colors focus:ring-2 focus:ring-offset-2 focus:ring-(--sb-secondary-border)"
        >
          Apply Dates
        </ButtonEl>
        <ButtonEl
          theme="secondary"
          onClick={onCancel}
          className="w-full py-3 rounded-full! font-bold text-sm uppercase tracking-wider hover:bg-gray-50 transition-colors focus:ring-2 focus:ring-offset-2 focus:ring-(--sb-secondary-border)"
        >
          Cancel
        </ButtonEl>
      </div>
    </div>
  )
}

export default CalendarPicker
